import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, Chip, Tabs } from '@heroui/react'
import { ApiError, api } from '../../lib/api'
import {
  addGroup, addQueue, addSegment, addWorkProfile, normaliseDoc, putGroup, putQueue, putWorkProfile,
  removeGroup, removeQueue, removeWorkProfile, sameDoc,
} from '../../lib/profilesDoc'
import { cardOf, pinProblems, problemText, type CardRef } from '../../lib/profilesProblems'
import { validateOperationDoc } from '../../lib/profilesValidate'
import { notLiveText } from '../../lib/branchConfig'
import { explain, refusedWith } from '../../lib/staffErrors'
import { passUnsaved, useUnsaved } from '../../lib/unsaved'
import { STAFF_LANGUAGES, nameIn, t } from '../../lib/staffText'
import type { OperationDoc, ProfileBranch, ProfileProblem, ProfileQueue, ProfileSegment, Service } from '../../lib/types'
import { Icon } from '../../ui/icons'
import { useMayRead } from '../../ui/Session'
import { useStaffText } from '../../ui/StaffText'
import { EquipmentAddSheet, EquipmentList } from '../equipment/EquipmentList'
import { openEquipment } from '../equipment/EquipmentSection'
import { setupChanged } from '../setupChanged'
import { Confirm, useFlash } from '../sectionKit'
import { AddRow, DangerCard, EditLayout, IconTile, Notice, PageHead, SaveActions, SummaryCard, useSubPage } from '../settingsKit'
import { AskBar } from '../setupKit'
import { BranchesStrip } from './BranchesStrip'
import { copyBase, copyNameWithin } from '../../lib/copyName'
import { NameSheet, ProfileMenu, refusalText } from './profileSheets'
import { GroupSheet } from './GroupSheet'
import { QueueSheet } from './QueueSheet'
import { Schematic, type Open } from './Schematic'
import { SegmentSheet } from './SegmentSheet'
import { SERVER_ONLY } from './sheetParts'
import { WorkProfileSheet } from './WorkProfileSheet'

type Tab = 'queuing' | 'equipment'
const TABS: readonly Tab[] = ['queuing', 'equipment']
const LISTED = 8

/** "New group", else "New group 2", … : the first name the scope does not hold (case aside). */
function freeName(base: string, taken: readonly string[]): string {
  const used = new Set(taken.map((n) => n.trim().toLowerCase()))
  if (!used.has(base.toLowerCase())) return base
  let n = 2
  while (used.has(`${base} ${n}`.toLowerCase())) n++
  return `${base} ${n}`
}

function freeCode(doc: OperationDoc): string {
  const used = new Set(doc.queues.map((q) => q.code.toUpperCase()))
  let n = doc.queues.length + 1
  while (used.has(`Q${n}`)) n++
  return `Q${n}`
}

/** Takes out a queue made with a group or rule that ended up pointing elsewhere. */
const dropUnused = (doc: OperationDoc, queueId: string | undefined): OperationDoc => {
  if (!queueId) return doc
  const next = removeQueue(doc, queueId)
  return Array.isArray(next) ? doc : next
}

/** The card open in a modal; `before` (a card just added) is the document to go back to on Cancel. */
type Editing = Open & { before?: OperationDoc; freshQueueId?: string }

const withSegments = (doc: OperationDoc, groupId: string, edit: (s: ProfileSegment[]) => ProfileSegment[]): OperationDoc => ({
  ...doc,
  groups: doc.groups.map((g) => (g.id === groupId ? { ...g, segments: edit(g.segments) } : g)),
})

type Loaded = { name: string; description: string; saved: OperationDoc; revision: number; branches: ProfileBranch[]; warnings: ProfileProblem[] }

/** One operation profile: the Queuing schematic with one Save, the Equipment tab, and its branches. */
export function ProfilePage({
  id,
  organisation,
  readOnly,
  onBack,
  onOpen,
  onDeleted,
}: {
  id: string
  organisation?: string
  readOnly: boolean
  onBack: () => void
  /** Opens another profile's page (a duplicate). */
  onOpen: (id: string) => void
  onDeleted: () => void
}) {
  const { lang, say } = useStaffText()
  const { flash, toast } = useFlash()
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const [doc, setDoc] = useState<OperationDoc | null>(null)
  const [services, setServices] = useState<Service[]>([])
  const [loadError, setLoadError] = useState('')
  const [tab, setTab] = useState<Tab>('queuing')
  const sub = useSubPage()
  // Cards open in modals over the page; the address and the scroll stay put.
  const [editing, setEditing] = useState<Editing | null>(null)
  const [naming, setNaming] = useState<'rename' | 'duplicate' | null>(null)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<{ problems: ProfileProblem[]; sent: OperationDoc } | null>(null)
  const [stale, setStale] = useState('')
  const [saveError, setSaveError] = useState('')
  const [leaving, setLeaving] = useState(false)
  const [addingEq, setAddingEq] = useState(false)

  const queuingDirty = !!loaded && !!doc && !sameDoc(doc, loaded.saved)
  const dirty = queuingDirty
  useUnsaved(dirty, sub.here || undefined)
  const dirtyRef = useRef(queuingDirty)
  dirtyRef.current = queuingDirty

  /** Reads the document. 'fresh' keeps the admin's edits when there are any and takes only the branches. */
  const load = useCallback(
    async (mode: 'all' | 'fresh') => {
      try {
        const answer = await api.operationDocument(id)
        const next: Loaded = {
          name: answer.profile.name,
          description: answer.profile.description,
          saved: normaliseDoc(answer.document),
          revision: answer.revision,
          branches: answer.branches ?? [],
          warnings: answer.warnings ?? [],
        }
        if (mode === 'fresh' && dirtyRef.current) {
          setLoaded((l) => (l ? { ...l, branches: next.branches } : next))
        } else {
          setLoaded(next)
          setDoc(next.saved)
        }
        setLoadError('')
      } catch (err) {
        setLoadError(explain(err, lang, 'common.unreachable'))
      }
    },
    [id, lang],
  )

  useEffect(() => {
    void load('all')
  }, [load])

  const readsServices = useMayRead('services')
  useEffect(() => {
    if (!readsServices) return
    api.catalogueServices(organisation).then(
      ({ services }) => setServices(services ?? []),
      () => setServices([]),
    )
  }, [organisation, readsServices])

  useEffect(() => {
    if (!dirty) return
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onUnload)
    return () => window.removeEventListener('beforeunload', onUnload)
  }, [dirty])

  // A refused save's problems as the document now stands: after an edit, what the
  // local check still finds, plus the server-only ones it cannot see.
  const refused = useMemo(() => {
    if (!failed || !doc) return []
    if (doc === failed.sent) return [failed]
    return [
      { problems: validateOperationDoc(doc).problems, sent: doc },
      { problems: failed.problems.filter((p) => SERVER_ONLY.has(p.code)), sent: failed.sent },
    ]
  }, [failed, doc])

  const pins = useMemo(() => {
    const all = new Map<string, ProfileProblem[]>()
    const merge = (from: Map<string, ProfileProblem[]>) => {
      for (const [k, v] of from) all.set(k, [...(all.get(k) ?? []), ...v])
    }
    for (const r of refused) merge(pinProblems(r.problems, r.sent))
    if (loaded) merge(pinProblems(loaded.warnings, loaded.saved))
    return all
  }, [refused, loaded])

  // Above the schematic: the refused save's problems, then the warnings, each opening its card.
  const listed = useMemo(() => {
    const items: Array<{ p: ProfileProblem; ref: CardRef }> = []
    for (const r of refused) for (const p of r.problems) items.push({ p, ref: cardOf(p.path, r.sent) })
    if (loaded) for (const p of loaded.warnings) items.push({ p, ref: cardOf(p.path, loaded.saved) })
    return items
  }, [refused, loaded])

  const crumbs = [{ label: say('profiles.section'), onOpen: () => (dirty ? setLeaving(true) : onBack()) }]
  if (!loaded || !doc) {
    return (
      <div className="pf-page">
        <PageHead crumbs={crumbs} title={say('profiles.allProfiles')} />
        {loadError ? <Notice>{loadError}</Notice> : <p className="empty">{say('common.loading')}</p>}
      </div>
    )
  }

  const edit = (change: (d: OperationDoc) => OperationDoc) => setDoc((d) => (d ? change(d) : d))
  const setOpen = (o: Open) => setEditing(o)
  const closeAll = () => setEditing(null)
  // Cancel, Escape or Remove on a card just added takes it back out.
  const cancel = () => {
    if (editing?.before) setDoc(editing.before)
    closeAll()
  }
  const finish = (change: (d: OperationDoc) => OperationDoc) => {
    edit(change)
    closeAll()
  }
  const removeOrCancel = (change: (d: OperationDoc) => OperationDoc) => (editing?.before ? cancel() : finish(change))

  const openOf = (ref: CardRef): Open | null => {
    if (ref.kind === 'doc') return null
    if (ref.kind === 'segment') {
      const g = doc.groups.find((x) => x.segments.some((s) => s.id === ref.id))
      return g ? { kind: 'segment', id: ref.id, groupId: g.id } : null
    }
    const there =
      ref.kind === 'group'
        ? doc.groups.some((x) => x.id === ref.id)
        : ref.kind === 'queue'
          ? doc.queues.some((x) => x.id === ref.id)
          : doc.workProfiles.some((x) => x.id === ref.id)
    return there ? { kind: ref.kind, id: ref.id } : null
  }

  const cardName = (o: Open): string => {
    if (o.kind === 'group') return doc.groups.find((g) => g.id === o.id)?.name ?? ''
    if (o.kind === 'segment') return doc.groups.flatMap((g) => g.segments).find((s) => s.id === o.id)?.name ?? ''
    if (o.kind === 'queue') {
      const q = doc.queues.find((x) => x.id === o.id)
      return q ? nameIn(q.name, lang, q.code) : ''
    }
    return doc.workProfiles.find((w) => w.id === o.id)?.name ?? ''
  }

  const queueNames = () => Object.fromEntries(STAFF_LANGUAGES.map((l) => [l, t('profiles.new.queue', l)]))

  const onAdd = (kind: 'group' | 'queue' | 'work' | 'segment', groupId?: string) => {
    if (kind === 'segment') {
      // Qmatic's "rule with queue": the rule arrives with a queue of its own.
      const g = doc.groups.find((x) => x.id === groupId)
      if (!g) return
      const [withQueue, qid] = addQueue(doc, queueNames(), freeCode(doc))
      const [withRule, sid] = addSegment(withQueue, g.id, freeName(say('profiles.new.segment'), g.segments.map((s) => s.name)))
      setDoc(withSegments(withRule, g.id, (list) => list.map((s) => (s.id === sid ? { ...s, queueId: qid } : s))))
      setEditing({ kind: 'segment', id: sid, groupId: g.id, before: doc, freshQueueId: qid })
    } else if (kind === 'group') {
      const name = freeName(say('profiles.new.group'), doc.groups.map((g) => g.name))
      const [next, gid] = addGroup(doc, name, queueNames(), freeCode(doc))
      setDoc(next)
      setEditing({ kind: 'group', id: gid, before: doc, freshQueueId: next.groups.find((g) => g.id === gid)?.defaultQueueId })
    } else if (kind === 'queue') {
      const [next, qid] = addQueue(doc, queueNames(), freeCode(doc))
      setDoc(next)
      setEditing({ kind: 'queue', id: qid, before: doc })
    } else {
      const [next, wid] = addWorkProfile(doc, freeName(say('profiles.new.work'), doc.workProfiles.map((w) => w.name)))
      setDoc(next)
      setEditing({ kind: 'work', id: wid, before: doc })
    }
  }

  const save = async () => {
    if (busy || readOnly || !queuingDirty) return
    setFailed(null)
    setStale('')
    setSaveError('')
    const sent = doc
    const { problems } = validateOperationDoc(sent)
    if (problems.length > 0) {
      setFailed({ problems, sent })
      return
    }
    setBusy(true)
    try {
      const res = await api.saveOperationDocument(id, { baseRevision: loaded.revision, document: sent })
      setLoaded((l) => (l ? { ...l, saved: sent, revision: res.revision, warnings: res.warnings ?? [] } : l))
      flash(say('common.saved'))
      setSaveError(notLiveText(res.notLive, lang))
      window.dispatchEvent(setupChanged())
      // The server added "All queues" to a profile's first queues: show it.
      void load(res.starterWorkProfile ? 'all' : 'fresh')
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && err.problems?.length) setFailed({ problems: err.problems, sent })
      else if (err instanceof ApiError && err.code === 'PROFILE_STALE') setStale(explain(err, lang, 'common.unreachable'))
      // A branch running this profile refused the change (CONFIG_WOULD_STRAND and the like).
      else setSaveError(refusedWith(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  const discardAndReload = () => {
    closeAll()
    setStale('')
    setFailed(null)
    void load('all')
  }

  const discard = () => {
    closeAll()
    setFailed(null)
    setStale('')
    setSaveError('')
    setDoc(loaded.saved)
  }

  const remove = async () => {
    setBusy(true)
    setSaveError('')
    try {
      await api.deleteOperationProfile(id)
      passUnsaved()
      onDeleted()
    } catch (err) {
      setSaveError(refusalText(err, lang))
      setBusy(false)
    }
  }


  // ---- modals ----
  const open = editing
  const group = open?.kind === 'group' ? doc.groups.find((g) => g.id === open.id) : undefined
  const segGroup = open?.kind === 'segment' ? doc.groups.find((g) => g.segments.some((s) => s.id === open.id)) : undefined
  const segment = segGroup?.segments.find((s) => s.id === open?.id)
  const queue = open?.kind === 'queue' ? doc.queues.find((q) => q.id === open.id) : undefined
  const work = open?.kind === 'work' ? doc.workProfiles.find((w) => w.id === open.id) : undefined
  const freshQueue = open?.freshQueueId ? doc.queues.find((q) => q.id === open.freshQueueId) : undefined
  const problemsOf = (cardId: string) => pins.get(cardId) ?? []
  const withQueue = (d: OperationDoc, q: ProfileQueue | undefined) => (q ? putQueue(d, q) : dropUnused(d, open?.freshQueueId))

  const shown = listed.slice(0, LISTED)
  const folded = listed.slice(LISTED)
  const item = ({ p, ref }: { p: ProfileProblem; ref: CardRef }, i: number) => {
    const target = openOf(ref)
    const text = problemText(p, lang)
    return (
      <li key={i} data-warn={p.warning || undefined}>
        {target ? (
          <button
            type="button"
            className="pf-issues__open"
            onClick={() => {
              setOpen(target)
            }}
          >
            <strong>{cardName(target)}</strong> {text}
          </button>
        ) : (
          <span>{text}</span>
        )}
      </li>
    )
  }


  return (
    <div className="pf-page">
      <div className="pf-body">
        <PageHead
          crumbs={crumbs}
          title={loaded.name}
          line={loaded.description || undefined}
          extra={
            dirty ? (
              <Chip size="sm" variant="soft" color="warning">
                {say('profiles.unsaved')}
              </Chip>
            ) : undefined
          }
          actions={
            <>
              <ProfileMenu profileId={id} readOnly={readOnly} onRename={() => setNaming('rename')} onDuplicate={() => setNaming('duplicate')} />
              {!readOnly && <SaveActions dirty={queuingDirty} busy={busy} onDiscard={discard} onSave={() => void save()} />}
            </>
          }
        />

        {leaving && (
          <AskBar
            className="pf-leave"
            title={say('ui.discardAsk')}
            actions={
              <>
                <Button variant="ghost" size="sm" autoFocus onPress={() => setLeaving(false)}>
                  {say('ui.keepEditing')}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onPress={() => {
                    passUnsaved()
                    onBack()
                  }}
                >
                  {say('ui.discard')}
                </Button>
              </>
            }
          />
        )}

        <Tabs className="pf-tabs" selectedKey={tab} onSelectionChange={(key) => setTab(String(key) as Tab)}>
          <Tabs.ListContainer>
            <Tabs.List aria-label={say('profiles.tabs')}>
              {TABS.map((k) => (
                <Tabs.Tab key={k} id={k}>
                  {say(k === 'queuing' ? 'profiles.queuing' : 'profiles.equipment')}
                  <Tabs.Indicator />
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs.ListContainer>
        </Tabs>

        <div role="tabpanel" aria-label={say('profiles.queuing')} className="pf-panel" hidden={tab !== 'queuing'}>
          {stale && (
            <AskBar
              role="alert"
              title={stale}
              actions={
                <Button variant="secondary" size="sm" onPress={discardAndReload}>
                  <Icon name="refresh" size={16} /> {say('profiles.reload')}
                </Button>
              }
            />
          )}
          {saveError && <Notice>{saveError}</Notice>}
          {listed.length > 0 && (
            <Notice tone={refused.some((r) => r.problems.some((p) => !p.warning)) ? 'danger' : 'warning'} className="pf-issues">
              <ul>{shown.map(item)}</ul>
              {folded.length > 0 && (
                <details>
                  <summary>{say('profiles.import.more', { n: folded.length })}</summary>
                  <ul>{folded.map((x, i) => item(x, LISTED + i))}</ul>
                </details>
              )}
            </Notice>
          )}
          <Card className="pf-board" role="region" aria-label={say('profiles.queuing')}>
            <Schematic
              doc={doc}
              services={services}
              pins={pins}
              readOnly={readOnly}
              onOpen={(o) => {
                setOpen(o)
              }}
              onAdd={onAdd}
            />
          </Card>
        </div>
        {tab === 'equipment' && (
          <div role="tabpanel" aria-label={say('profiles.equipment')} className="pf-panel">
            <EquipmentList
              operationProfileId={id}
              onOpen={openEquipment}
              foot={!readOnly && <AddRow label={say('eq.tab.add')} onClick={() => setAddingEq(true)} />}
            />
          </div>
        )}

        <div className="pf-lower">
          <EditLayout
            side={
              <>
                <SummaryCard
                  band={say('settings.op.band')}
                  tile={<IconTile icon="filter" size="xl" />}
                  name={loaded.name}
                  sub={loaded.description || undefined}
                  stats={[
                    { label: say('settings.op.queues'), value: doc.queues.length },
                    { label: say('settings.op.groups'), value: doc.groups.length },
                    { label: say('settings.col.branches'), value: loaded.branches.length },
                  ]}
                />
                {!readOnly && (
                  <DangerCard
                    title={say('settings.op.deleteTitle')}
                    line={say('settings.op.deleteLine')}
                    action={
                      <Confirm
                        ask={say('common.delete')}
                        question={say('profiles.list.deleteAsk')}
                        yes={say('common.delete')}
                        keep={say('common.keep')}
                        busy={busy}
                        onYes={() => void remove()}
                      />
                    }
                  />
                )}
              </>
            }
          >
            <BranchesStrip branches={loaded.branches} />
          </EditLayout>
        </div>
      </div>

      {group && (
        <GroupSheet
          key={group.id}
          doc={doc}
          group={group}
          freshQueue={freshQueue}
          services={services}
          problems={problemsOf(group.id)}
          readOnly={readOnly}
          onApply={(g, q) => finish((d) => withQueue(putGroup(d, g), q))}
          onRemove={() => removeOrCancel((d) => removeGroup(d, group.id))}
          onClose={cancel}
        />
      )}
      {segGroup && segment && (
        <SegmentSheet
          key={segment.id}
          doc={doc}
          groupId={segGroup.id}
          segment={segment}
          freshQueue={freshQueue}
          services={services}
          problems={problemsOf(segment.id)}
          readOnly={readOnly}
          onApply={(s, q) => finish((d) => withQueue(withSegments(d, segGroup.id, (list) => list.map((x) => (x.id === s.id ? s : x))), q))}
          onRemove={() => removeOrCancel((d) => withSegments(d, segGroup.id, (list) => list.filter((x) => x.id !== segment.id)))}
          onClose={cancel}
        />
      )}
      {queue && (
        <QueueSheet
          key={queue.id}
          doc={doc}
          queue={queue}
          problems={problemsOf(queue.id)}
          readOnly={readOnly}
          onApply={(q) => finish((d) => putQueue(d, q))}
          onRemove={() => removeOrCancel((d) => dropUnused(d, queue.id))}
          onClose={cancel}
        />
      )}
      {work && (
        <WorkProfileSheet
          key={work.id}
          doc={doc}
          profile={work}
          problems={problemsOf(work.id)}
          readOnly={readOnly}
          onApply={(w) => finish((d) => putWorkProfile(d, w))}
          onRemove={() => removeOrCancel((d) => removeWorkProfile(d, work.id))}
          onClose={cancel}
        />
      )}
      {naming === 'rename' && (
        <NameSheet
          title={loaded.name}
          submitLabel={say('common.save')}
          initial={{ name: loaded.name, description: loaded.description }}
          onClose={() => setNaming(null)}
          onSave={async (v) => {
            await api.updateOperationProfile(id, v)
            setLoaded((l) => (l ? { ...l, name: v.name, description: v.description } : l))
            setNaming(null)
            flash(say('common.saved'))
          }}
        />
      )}
      {naming === 'duplicate' && (
        <NameSheet
          title={say('profiles.list.duplicate')}
          submitLabel={say('profiles.list.duplicate')}
          initial={{ name: copyNameWithin(copyBase(loaded.name), (name) => say('profiles.copyName', { name })), description: '' }}
          hideDescription
          onClose={() => setNaming(null)}
          onSave={async (v) => {
            const { profile } = await api.duplicateOperationProfile(id, { name: v.name })
            onOpen(profile.id)
          }}
        />
      )}
      {addingEq && (
        <EquipmentAddSheet
          operationProfileId={id}
          onClose={() => setAddingEq(false)}
          onMade={(eid) => {
            setAddingEq(false)
            openEquipment(eid)
          }}
        />
      )}
      {toast}
    </div>
  )
}
